import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface RelatedData {
  routes: any[];
  runs: any[];
  segments: any[];
  accessPoints: any[];
  isLoading: boolean;
  error: string | null;
}

// Hook to fetch routes that use a specific trail segment
export const useRelatedRoutes = (segmentId?: string) => {
  const [routes, setRoutes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!segmentId) {
      setRoutes([]);
      return;
    }

    const fetchRelatedRoutes = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('route_segments')
          .select(`
            route_id,
            routes (
              id,
              name,
              description,
              length_miles,
              difficulty,
              created_at,
              created_by
            )
          `)
          .eq('trail_segment_id', segmentId);

        if (error) throw error;

        const uniqueRoutes = data?.reduce((acc, item) => {
          if (item.routes && !acc.find(r => r.id === item.routes.id)) {
            acc.push(item.routes);
          }
          return acc;
        }, [] as any[]) || [];

        setRoutes(uniqueRoutes);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRelatedRoutes();
  }, [segmentId]);

  return { routes, isLoading, error };
};

// Hook to fetch runs that covered a specific trail segment
export const useRelatedRuns = (segmentId?: string) => {
  const [runs, setRuns] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!segmentId) {
      setRuns([]);
      return;
    }

    const fetchRelatedRuns = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('run_segments')
          .select(`
            run_id,
            runs!inner (
              id,
              equipment_type,
              distance_miles,
              duration_minutes,
              created_at,
              user_id,
              visibility
            )
          `)
          .eq('trail_segment_id', segmentId)
          .in('runs.visibility', ['public', 'anonymous']);

        if (error) throw error;

        const uniqueRuns = data?.reduce((acc, item) => {
          if (item.runs && !acc.find(r => r.id === item.runs.id)) {
            acc.push(item.runs);
          }
          return acc;
        }, [] as any[]) || [];

        setRuns(uniqueRuns);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRelatedRuns();
  }, [segmentId]);

  return { runs, isLoading, error };
};

// Hook to fetch trail segments that make up a specific route
export const useRouteSegments = (routeId?: string) => {
  const [segments, setSegments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!routeId) {
      setSegments([]);
      return;
    }

    const fetchRouteSegments = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('route_segments')
          .select(`
            segment_order,
            trail_segments (
              id,
              trail_name,
              difficulty,
              length_miles,
              surface_type,
              geometry,
              forest_name,
              district_name
            )
          `)
          .eq('route_id', routeId)
          .order('segment_order');

        if (error) throw error;

        const routeSegments = data?.map(item => item.trail_segments).filter(Boolean) || [];
        setSegments(routeSegments);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRouteSegments();
  }, [routeId]);

  return { segments, isLoading, error };
};

// Hook to fetch trail segments covered in a specific run
export const useRunSegments = (runId?: string) => {
  const [segments, setSegments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) {
      setSegments([]);
      return;
    }

    const fetchRunSegments = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('run_segments')
          .select(`
            segment_order,
            trail_segments (
              id,
              trail_name,
              difficulty,
              length_miles,
              surface_type,
              geometry,
              forest_name,
              district_name
            )
          `)
          .eq('run_id', runId)
          .order('segment_order');

        if (error) throw error;

        const runSegments = data?.map(item => item.trail_segments).filter(Boolean) || [];
        setSegments(runSegments);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRunSegments();
  }, [runId]);

  return { segments, isLoading, error };
};

// Hook to fetch access points near a trail segment
export const useNearbyAccessPoints = (segmentId?: string) => {
  const [accessPoints, setAccessPoints] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!segmentId) {
      setAccessPoints([]);
      return;
    }

    const fetchNearbyAccessPoints = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('access_points')
          .select(`
            id,
            name,
            description,
            amenities,
            geom
          `)
          .eq('trail_segment_id', segmentId);

        if (error) throw error;
        setAccessPoints(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNearbyAccessPoints();
  }, [segmentId]);

  return { accessPoints, isLoading, error };
};

// Hook to fetch all routes that use ANY segment of a specific trail
export const useTrailRoutes = (trailId?: string) => {
  const [routes, setRoutes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!trailId) {
      setRoutes([]);
      return;
    }

    const fetchTrailRoutes = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('route_segments')
          .select(`
            route_id,
            routes (
              id,
              name,
              description,
              length_miles,
              difficulty,
              created_at,
              created_by
            ),
            trail_segments!inner (
              trail_id
            )
          `)
          .eq('trail_segments.trail_id', trailId);

        if (error) throw error;

        const uniqueRoutes = data?.reduce((acc, item) => {
          if (item.routes && !acc.find(r => r.id === item.routes.id)) {
            acc.push(item.routes);
          }
          return acc;
        }, [] as any[]) || [];

        setRoutes(uniqueRoutes);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrailRoutes();
  }, [trailId]);

  return { routes, isLoading, error };
};

// Hook to fetch all runs that covered ANY segment of a specific trail
export const useTrailRuns = (trailId?: string) => {
  const [runs, setRuns] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!trailId) {
      setRuns([]);
      return;
    }

    const fetchTrailRuns = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('run_segments')
          .select(`
            run_id,
            runs!inner (
              id,
              equipment_type,
              distance_miles,
              duration_minutes,
              start_time,
              end_time,
              created_at,
              user_id,
              visibility
            ),
            trail_segments!inner (
              trail_id
            )
          `)
          .eq('trail_segments.trail_id', trailId)
          .in('runs.visibility', ['public', 'anonymous'])
          .order('runs(end_time)', { ascending: false });

        if (error) throw error;

        const uniqueRuns = data?.reduce((acc, item) => {
          if (item.runs && !acc.find(r => r.id === item.runs.id)) {
            acc.push(item.runs);
          }
          return acc;
        }, [] as any[]) || [];

        setRuns(uniqueRuns);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrailRuns();
  }, [trailId]);

  return { runs, isLoading, error };
};

// Combined hook for getting all related data
export const useRelatedData = (
  itemId?: string,
  itemType?: 'segment' | 'route' | 'run'
): RelatedData => {
  const relatedRoutes = useRelatedRoutes(itemType === 'segment' ? itemId : undefined);
  const relatedRuns = useRelatedRuns(itemType === 'segment' ? itemId : undefined);
  const routeSegments = useRouteSegments(itemType === 'route' ? itemId : undefined);
  const runSegments = useRunSegments(itemType === 'run' ? itemId : undefined);
  const nearbyAccessPoints = useNearbyAccessPoints(itemType === 'segment' ? itemId : undefined);

  const isLoading = relatedRoutes.isLoading || relatedRuns.isLoading || 
                   routeSegments.isLoading || runSegments.isLoading || 
                   nearbyAccessPoints.isLoading;

  const error = relatedRoutes.error || relatedRuns.error || 
               routeSegments.error || runSegments.error || 
               nearbyAccessPoints.error;

  return {
    routes: itemType === 'segment' ? relatedRoutes.routes : [],
    runs: itemType === 'segment' ? relatedRuns.runs : [],
    segments: itemType === 'route' ? routeSegments.segments : 
              itemType === 'run' ? runSegments.segments : [],
    accessPoints: itemType === 'segment' ? nearbyAccessPoints.accessPoints : [],
    isLoading,
    error
  };
};